<!--
  FusionAdminApp.vue — the frame of the Fusion Meals admin (admin.fusion-eat.com).

  The console inside is the Pizza Felice app's own (views/meals/MealsHome.vue),
  so this frame gives it what the Pizza Felice frame gave it: the bar with the
  page's section and client chip (useAppBarActions), the reports menu
  (MealsMenu, opened from the bar's dots), the add (+) button, the shared
  confirm dialog and the "booked in the GL" messages. No sidebar: there is one
  module here.
-->
<template>
  <v-app>
    <TestSiteBar />
    <v-app-bar v-if="signedIn" color="#b80f0a" density="comfortable" flat>
      <v-img :src="logoWhite" alt="Fusion Eat" width="96" height="36" class="ms-3 flex-grow-0" />
      <v-app-bar-title>
        <span v-if="pageSection" class="font-weight-medium">{{ pageSection.title }}</span>
        <v-chip v-if="infoChip" size="x-small" variant="tonal" class="ms-2">
          <v-icon start size="x-small">{{ infoChip.icon || "mdi-domain" }}</v-icon>
          {{ infoChip.chip }}
        </v-chip>
      </v-app-bar-title>
      <template v-slot:append>
        <v-btn v-if="route.name === 'MealsHome'" icon variant="text" @click="showMealsMenu = true">
          <v-icon>mdi-dots-vertical</v-icon>
          <v-tooltip activator="parent" location="bottom">All reports</v-tooltip>
        </v-btn>
        <v-menu location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn icon variant="text" v-bind="props">
              <v-icon>mdi-account-circle-outline</v-icon>
              <v-tooltip activator="parent" location="bottom">{{ userEmail }}</v-tooltip>
            </v-btn>
          </template>
          <v-list density="compact" nav>
            <v-list-item :title="userEmail" subtitle="Signed in" />
            <v-divider class="my-1" />
            <v-list-item :title="isDark ? 'Light theme' : 'Dark theme'" @click="toggleTheme">
              <template v-slot:prepend><v-icon :icon="isDark ? 'mdi-weather-sunny' : 'mdi-weather-night'" /></template>
            </v-list-item>
            <v-list-item title="Log out" @click="logout">
              <template v-slot:prepend><v-icon icon="mdi-logout" /></template>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-app-bar>

    <v-main>
      <router-view />

      <!-- The page's add (+) action, as in the Pizza Felice frame (App.vue) -->
      <v-fab
        v-if="fabAction"
        app
        location="bottom right"
        size="large"
        icon
        color="primary"
        :loading="fabAction.loading"
        @click="fabAction.onClick"
      >
        <v-icon>mdi-plus</v-icon>
        <v-tooltip activator="parent" location="left">{{ fabAction.tooltip }}</v-tooltip>
      </v-fab>

      <meals-menu v-if="signedIn" />
      <confirm-dialog />
      <booked-snackbar />
    </v-main>
  </v-app>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useTheme } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import { startTokenRefreshTimer, isAuthenticated, logout } from "@/utils/api.js";
import MealsMenu from "@/components/MealsMenu.vue";
import TestSiteBar from "@/components/TestSiteBar.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import BookedSnackbar from "@/components/BookedSnackbar.vue";
import logoWhite from "./assets/logo-white.png";

const route = useRoute();
const theme = useTheme();
const { showMealsMenu } = useApp();
const { actions, pageSection } = useAppBarActions();

// The frame shows once past the login pages — and only to someone signed in,
// so it never flashes over the blank page before the redirect to the login
const signedIn = computed(() => !route.meta.public && route.name !== "ForcePasswordChange" && route.matched.length > 0 && isAuthenticated());

// The client chip MealsHome sets, and its add (+) action shown as the FAB
const infoChip = computed(() => actions.value.find((a) => a.show !== false && a.chip));
const fabAction = computed(() => actions.value.find((a) => a.show !== false && !a.chip && a.icon === "mdi-plus"));

const userEmail = computed(() => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.email || "";
  } catch {
    return "";
  }
});

const isDark = computed(() => theme.global.current.value.dark);
function toggleTheme() {
  const next = isDark.value ? "light" : "dark";
  theme.global.name.value = next;
  localStorage.setItem("theme", next);
}

onMounted(() => {
  if (isAuthenticated()) startTokenRefreshTimer();
});
</script>
